// Client → server intents (protocol §5.1, §7, movement.md §4.2, §4.4):
// - INPUT: on every display frame in which the target moved > 0.5 u since the last INPUT (agar
//   sends at display rate; the old once-per-40 ms window added up to 40 ms of loop delay), at
//   most one per `INPUT_MIN_GAP_MS`; a 250 ms heartbeat when it has not moved. Sent only while
//   `steering` (playing with the menu closed, or free-roam spectating); the menu sends none. The
//   server keeps only the newest INPUT per tick and charges one rate-limit token per tick, so a
//   superseded INPUT is never a drop (protocol §9.3).
// - SPLIT / EJECT / SPECIAL / SPECTATE / PLAY: one message per call.
// - Hold W (setting): EJECT every 80 ms while held (`update` drives the repeat).
// - OPTIONS: after WELCOME and whenever a flag or the zoom (rounded to 0.01) changes, at most
//   5 per second (a change inside the window is sent when it reopens).
//
// Target, in order of precedence each `update`:
// 1. `ctx.target`: a per-frame world target from another device (input/steer.ts: touch joystick,
//    follow finger, gamepad, the pre-pointer rest). Recomputed by its source every frame.
// 2. An explicit `setTargetWorld` target, which holds until the pointer moves again (one-shot
//    uses only).
// 3. The pointer. Anchor (movement.md §4.2): `'camera'` (agar's model, default) →
//    `camera.screenToWorld(pointer)`; `'server'` → `ownNewest + (pointerWorld − camera centre)`,
//    which takes the interpolation and camera lag out of the loop. With `anchorPolicy 'auto'`
//    the sender switches to `'server'` when the smoothed RTT exceeds 90 ms and back below 70 ms
//    (hysteresis); without `ctx.ownNewest` (spectating) it is always the camera.
//
// Details (decided here):
// - SPLIT and EJECT first flush a pending target change as an INPUT, so a split goes where the
//   cursor points now.
// - Non-finite targets are never sent (the server closes 4004 on them).
//
// Implementer: client input/UI. Consumers: app.ts (calls from the main loop and key handlers).

import {
  clamp,
  encodeEject,
  encodeInput,
  encodeOptions,
  encodePlay,
  encodeSpecial,
  encodeSpectate,
  encodeSplit,
  NET_TIMING,
  type Vec2,
  WHEEL_ZOOM_WIRE_MAX,
  WHEEL_ZOOM_WIRE_MIN,
} from '@ballio/shared';
import type { Camera } from '../game/camera.js';

/** Where messages go (GameSocket implements it). */
export interface MessageSink {
  send(bytes: Uint8Array<ArrayBuffer>): void;
}

export interface OptionsState {
  readonly wantMinimapPlayers: boolean;
  readonly menuOpen: boolean;
  readonly wheelZoom: number;
}

/** Smallest gap between two INPUTs: display rate up to ~166 Hz, every other frame at 240 Hz. */
export const INPUT_MIN_GAP_MS = 6;

/** RTT hysteresis of the auto anchor (movement.md §4.2). */
export const ANCHOR_SERVER_RTT_MS = 90;
export const ANCHOR_CAMERA_RTT_MS = 70;

export type SteerAnchor = 'camera' | 'server';
export type AnchorPolicy = 'auto' | SteerAnchor;

/** Per-frame steering facts (input/steer.ts SteerController fills it). */
export interface SteerContext {
  /** The own r²-weighted centroid in the newest FRAME (`store.ownNewest()`), or null. */
  readonly ownNewest?: Readonly<Vec2> | null;
  /** Smoothed RTT in ms (`socket.rttMs`); 0 or missing = unknown (the anchor is kept). */
  readonly rttMs?: number;
  /** A world target computed by another device this frame; overrides the pointer. */
  readonly target?: Readonly<Vec2> | null;
}

/** INPUTs remembered for own-motion prediction (input/steer.ts): ~0.8 s at 60 Hz. */
export const SENT_LOG_SIZE = 48;

/**
 * The recent INPUT targets as sent (local ms, world x/y), oldest first: input/steer.ts replays
 * them through the server's steering law to predict where the own cell will be when the next
 * INPUT lands (movement.md §4.3).
 */
export class SentLog {
  private readonly t = new Float64Array(SENT_LOG_SIZE);
  private readonly x = new Float64Array(SENT_LOG_SIZE);
  private readonly y = new Float64Array(SENT_LOG_SIZE);
  private head = 0;
  private count = 0;

  get size(): number {
    return this.count;
  }

  record(atMs: number, x: number, y: number): void {
    this.t[this.head] = atMs;
    this.x[this.head] = x;
    this.y[this.head] = y;
    this.head = (this.head + 1) % SENT_LOG_SIZE;
    if (this.count < SENT_LOG_SIZE) this.count++;
  }

  /**
   * The newest target sent at or before `atMs` into `out` (true), or false when every logged
   * INPUT is newer (or none was sent).
   */
  latestAtOrBefore(atMs: number, out: Vec2): boolean {
    for (let k = 1; k <= this.count; k++) {
      const i = (this.head - k + SENT_LOG_SIZE) % SENT_LOG_SIZE;
      if ((this.t[i] as number) <= atMs) {
        out.x = this.x[i] as number;
        out.y = this.y[i] as number;
        return true;
      }
    }
    return false;
  }

  clear(): void {
    this.head = 0;
    this.count = 0;
  }
}

/** Hold-W repeat interval (ui-flow §4.5, mechanics §9). */
export const HOLD_EJECT_MS = 80;

/**
 * Minimum gap between two OPTIONS (protocol §5.1: at most 5 per second), with a little slack so
 * the server's per-second limit is never hit by timer granularity.
 */
export const OPTIONS_MIN_INTERVAL_MS = Math.ceil(1000 / NET_TIMING.optionsMaxPerSec) + 10;

/** Targets are kept inside this range (far beyond the map; keeps f32 values sane). */
const MAX_TARGET_ABS = 1e7;

/** An explicit target is dropped once the pointer moves more than this (CSS px). */
const POINTER_MOVE_EPS_PX = 1;

interface SentOptions {
  readonly flags: number;
  readonly zoom100: number;
}

function optionsKey(o: OptionsState): SentOptions {
  const z = Number.isFinite(o.wheelZoom) ? Math.round(o.wheelZoom * 100) : 100;
  return {
    flags: (o.wantMinimapPlayers ? 1 : 0) | (o.menuOpen ? 2 : 0),
    zoom100: clamp(z, WHEEL_ZOOM_WIRE_MIN, WHEEL_ZOOM_WIRE_MAX),
  };
}

export class InputSender {
  /** INPUT is sent only while true (app sets it per state). */
  steering = false;
  /** How the pointer is anchored (movement.md §4.2); 'auto' follows the RTT. */
  anchorPolicy: AnchorPolicy = 'auto';
  protected readonly sink: MessageSink;
  /** ms per server tick (from WELCOME). Informational: the INPUT cadence no longer uses it. */
  tickMs: number;
  /** The INPUTs sent lately (for own-motion prediction). */
  readonly sentLog = new SentLog();

  private readonly scratch: Vec2 = { x: 0, y: 0 };
  private readonly targetView: Vec2 = { x: 0, y: 0 };
  /** The current target in world units. */
  private tx = 0;
  private ty = 0;
  private hasTarget = false;
  /** The target was set with setTargetWorld and the pointer has not moved since. */
  private explicit = false;
  private explicitPx = Number.NaN;
  private explicitPy = Number.NaN;
  private lastPx = Number.NaN;
  private lastPy = Number.NaN;

  /** The last INPUT sent. */
  private sentX = 0;
  private sentY = 0;
  private hasSent = false;
  private lastSendAt = Number.NEGATIVE_INFINITY;
  /** The `nowMs` of the last `update` (timestamps INPUTs flushed by SPLIT/EJECT). */
  private lastNow = 0;
  /** The auto anchor's state (RTT hysteresis). */
  private autoServer = false;

  private ejectHeld = false;
  private holdEnabled = false;
  private nextEjectAt = 0;

  private wanted: OptionsState | null = null;
  private sentOptions: SentOptions | null = null;
  private lastOptionsAt = Number.NEGATIVE_INFINITY;
  private forceOptions = false;

  constructor(sink: MessageSink, tickMs = 40) {
    this.sink = sink;
    this.tickMs = tickMs;
  }

  /** The anchor the pointer uses now (before any `update` with an RTT: 'camera' under 'auto'). */
  get anchor(): SteerAnchor {
    if (this.anchorPolicy !== 'auto') return this.anchorPolicy;
    return this.autoServer ? 'server' : 'camera';
  }

  /** The current steering target (world units), or null before the first one. */
  get target(): Readonly<Vec2> | null {
    if (!this.hasTarget) return null;
    this.targetView.x = this.tx;
    this.targetView.y = this.ty;
    return this.targetView;
  }

  /**
   * Per display frame: the target by the precedence above; INPUT by the cadence rules; pending
   * OPTIONS and hold-W repeats. A non-finite pointer means "no pointer" (the target stays where
   * it was).
   */
  update(
    nowMs: number,
    camera: Camera,
    pointerX: number,
    pointerY: number,
    ctx?: SteerContext,
  ): void {
    this.lastNow = nowMs;
    this.updateAnchor(ctx?.rttMs);
    const forced = ctx?.target ?? null;
    if (forced && Number.isFinite(forced.x) && Number.isFinite(forced.y)) {
      this.explicit = false;
      this.setTarget(forced.x, forced.y);
    } else if (Number.isFinite(pointerX) && Number.isFinite(pointerY)) {
      if (this.explicit) {
        if (Number.isNaN(this.explicitPx)) {
          this.explicitPx = pointerX;
          this.explicitPy = pointerY;
        } else if (
          Math.abs(pointerX - this.explicitPx) > POINTER_MOVE_EPS_PX ||
          Math.abs(pointerY - this.explicitPy) > POINTER_MOVE_EPS_PX
        ) {
          this.explicit = false;
        }
      }
      this.lastPx = pointerX;
      this.lastPy = pointerY;
      if (!this.explicit) {
        const w = camera.screenToWorld(pointerX, pointerY, this.scratch);
        const own = ctx?.ownNewest ?? null;
        if (own && this.anchor === 'server' && Number.isFinite(own.x) && Number.isFinite(own.y)) {
          this.setTarget(own.x + (w.x - camera.x), own.y + (w.y - camera.y));
        } else {
          this.setTarget(w.x, w.y);
        }
      }
    }

    if (this.steering) {
      this.maybeSendInput(nowMs);
      if (this.ejectHeld && this.holdEnabled && nowMs >= this.nextEjectAt) {
        this.sendEject();
        this.nextEjectAt =
          nowMs - this.nextEjectAt > HOLD_EJECT_MS
            ? nowMs + HOLD_EJECT_MS
            : this.nextEjectAt + HOLD_EJECT_MS;
      }
    }
    this.flushOptions(nowMs);
  }

  /**
   * Sets the target directly in world units; it holds until the pointer moves again. For
   * one-shot uses only: per-frame devices pass `ctx.target` to `update` instead.
   */
  setTargetWorld(x: number, y: number): void {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    this.setTarget(x, y);
    this.explicit = true;
    this.explicitPx = this.lastPx;
    this.explicitPy = this.lastPy;
  }

  split(): void {
    this.flushTarget();
    this.sink.send(encodeSplit());
  }

  eject(): void {
    this.flushTarget();
    this.sendEject();
  }

  /** W held / released; repeats every 80 ms only when `holdEnabled`. */
  setEjectHeld(held: boolean, holdEnabled: boolean, nowMs: number): void {
    if (held) {
      if (!this.ejectHeld) this.nextEjectAt = nowMs + HOLD_EJECT_MS;
      this.ejectHeld = true;
      this.holdEnabled = holdEnabled;
    } else {
      this.ejectHeld = false;
    }
  }

  special(): void {
    this.sink.send(encodeSpecial());
  }

  spectate(): void {
    this.sink.send(encodeSpectate());
  }

  /** PLAY with the raw nick (the server sanitises) and the resolved skin id (name skins included). */
  play(nick: string, skinId: string): void {
    this.sink.send(encodePlay(nick, skinId));
  }

  /** Records the wanted OPTIONS; sent when changed (rate-limited). */
  setOptions(opts: OptionsState, nowMs: number): void {
    this.wanted = {
      wantMinimapPlayers: opts.wantMinimapPlayers,
      menuOpen: opts.menuOpen,
      wheelZoom: opts.wheelZoom,
    };
    this.flushOptions(nowMs);
  }

  /** New socket (after WELCOME) or feedPaused → next user input: send the current OPTIONS now. */
  resendOptions(nowMs: number): void {
    this.forceOptions = true;
    this.flushOptions(nowMs);
  }

  /** Forget the last-sent target and timers (new socket). */
  reset(): void {
    this.hasSent = false;
    this.lastSendAt = Number.NEGATIVE_INFINITY;
    this.sentLog.clear();
    this.autoServer = false;
    this.ejectHeld = false;
    this.nextEjectAt = 0;
    this.sentOptions = null;
    this.lastOptionsAt = Number.NEGATIVE_INFINITY;
    this.forceOptions = false;
  }

  // -------------------------------------------------------------------------------------------

  private setTarget(x: number, y: number): void {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    this.tx = clamp(x, -MAX_TARGET_ABS, MAX_TARGET_ABS);
    this.ty = clamp(y, -MAX_TARGET_ABS, MAX_TARGET_ABS);
    this.hasTarget = true;
  }

  private movedSinceSent(): boolean {
    if (!this.hasSent) return true;
    const dx = this.tx - this.sentX;
    const dy = this.ty - this.sentY;
    const min = NET_TIMING.inputMinMoveU;
    return dx * dx + dy * dy > min * min;
  }

  private updateAnchor(rttMs: number | undefined): void {
    if (rttMs === undefined || !(rttMs > 0) || !Number.isFinite(rttMs)) return;
    if (!this.autoServer && rttMs > ANCHOR_SERVER_RTT_MS) this.autoServer = true;
    else if (this.autoServer && rttMs < ANCHOR_CAMERA_RTT_MS) this.autoServer = false;
  }

  private maybeSendInput(nowMs: number): void {
    if (!this.hasTarget) return;
    const since = nowMs - this.lastSendAt;
    const heartbeat = since >= NET_TIMING.inputHeartbeatMs;
    if (heartbeat || (since >= INPUT_MIN_GAP_MS && this.movedSinceSent())) this.sendInput(nowMs);
  }

  /** Before SPLIT/EJECT: send the newest target if it differs from the last INPUT. */
  private flushTarget(): void {
    if (!this.steering || !this.hasTarget || !this.movedSinceSent()) return;
    this.sink.send(encodeInput(this.tx, this.ty));
    this.sentX = this.tx;
    this.sentY = this.ty;
    this.hasSent = true;
    this.sentLog.record(this.lastNow, this.tx, this.ty);
  }

  private sendInput(nowMs: number): void {
    this.sink.send(encodeInput(this.tx, this.ty));
    this.sentX = this.tx;
    this.sentY = this.ty;
    this.hasSent = true;
    this.lastSendAt = nowMs;
    this.sentLog.record(nowMs, this.tx, this.ty);
  }

  private sendEject(): void {
    this.sink.send(encodeEject());
  }

  private flushOptions(nowMs: number): void {
    const wanted = this.wanted;
    if (!wanted) return;
    const key = optionsKey(wanted);
    const sent = this.sentOptions;
    const changed = !sent || sent.flags !== key.flags || sent.zoom100 !== key.zoom100;
    if (!changed && !this.forceOptions) return;
    if (nowMs - this.lastOptionsAt < OPTIONS_MIN_INTERVAL_MS) return; // sent from a later update
    this.sink.send(
      encodeOptions({
        wantMinimapPlayers: wanted.wantMinimapPlayers,
        menuOpen: wanted.menuOpen,
        wheelZoom: key.zoom100 / 100,
      }),
    );
    this.sentOptions = key;
    this.lastOptionsAt = nowMs;
    this.forceOptions = false;
  }
}
